/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
 
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getFriendlyErrorMessage(error: unknown, context: string): string {
    let rawMessage = 'Đã xảy ra lỗi không xác định.';
    if (error instanceof Error) {
        rawMessage = error.message;
    } else if (typeof error === 'string') {
        rawMessage = error;
    } else if (error) {
        rawMessage = String(error);
    }

    // Check for specific unsupported MIME type error from Gemini API
    if (rawMessage.includes("Unsupported MIME type")) {
        try {
            // It might be a JSON string like '{"error":{"message":"..."}}'
            const errorJson = JSON.parse(rawMessage);
            const nestedMessage = errorJson?.error?.message;
            if (nestedMessage && nestedMessage.includes("Unsupported MIME type")) {
                const mimeType = nestedMessage.split(': ')[1] || 'không hỗ trợ';
                return `Định dạng tệp '${mimeType}' không được hỗ trợ. Vui lòng sử dụng PNG, JPEG hoặc WEBP.`;
            }
        } catch (e) {
            // Not a JSON string, but contains the text. Fallthrough to generic message.
        }
        // Generic fallback for any "Unsupported MIME type" error
        return `Định dạng tệp không được hỗ trợ. Vui lòng sử dụng PNG, JPEG hoặc WEBP.`;
    }
    
    return `${context}. ${rawMessage}`;
}

/**
 * Render text to image with auto-crop and multiline support
 * @param text - Text content (supports \n for line breaks)
 * @param fontFamily - Font family name
 * @param textAlign - Text alignment: 'left', 'center', or 'right'
 * @param color - Text color in hex format (default: #000000)
 * @returns Base64 PNG data URL
 */
export const renderLogoToImage = (
    text: string,
    fontFamily: string = "Inter",
    textAlign: 'left' | 'center' | 'right' = 'center',
    color: string = "#000000"
): string => {
    // 1. Create large temporary canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return "";

    canvas.width = 2500;
    canvas.height = 2500;

    // Transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set text color (default: black)
    ctx.fillStyle = color;
    ctx.textBaseline = "top";

    // Mapping font family name to CSS-compatible font stack
    // Priority: Use the exact fontFamily first, then fallback to generic family
    let fontName = fontFamily;

    // Map specific fonts to their best generic family or exact name
    const fontMapping: Record<string, string> = {
      // Signature fonts
      "SVN-Signature": "'SVN-Signature', cursive",
      "SVN-Honeymoon": "'SVN-Honeymoon', cursive",
      "Pacifico": "'Pacifico', cursive",
      "Crocante": "'Crocante', cursive",
      "SVN-Sofia": "'SVN-Sofia', serif",

      // Brand/Slogan fonts
      "SVN-Gilroy": "'SVN-Gilroy', sans-serif",
      "Be Vietnam Pro": "'Be Vietnam Pro', sans-serif",
      "SVN-Poppins": "'SVN-Poppins', sans-serif",
      "SVN-Montserrat": "'SVN-Montserrat', sans-serif",
      "Helvetica Neue": "'Helvetica Neue', Helvetica, sans-serif",
      "Playfair Display": "'Playfair Display', serif",
      "SVN-Butler": "'SVN-Butler', serif",
      "SVN-Bodoni": "'SVN-Bodoni', serif",
      "SVN-Cormorant": "'SVN-Cormorant', serif",

      // Logo/Headline fonts
      "SVN-Bebas Neue": "'SVN-Bebas Neue', sans-serif",
      "SVN-Oswald": "'SVN-Oswald', sans-serif",
      "SVN-Anton": "'SVN-Anton', sans-serif",

      // Legacy support
      "Inter": "sans-serif",
      "Courier": "monospace",
      "bold": "sans-serif",
      "regular": "sans-serif",
      "elegant": "serif",
      "modern": "sans-serif",
    };

    fontName = fontMapping[fontFamily] || `'${fontFamily}', sans-serif`;

    // Split text into lines
    const lines = text.split('\n').filter(line => line.trim().length > 0);
    
    // Auto-fit algorithm for maximum font size
    let fontSize = 600;
    const maxWidth = 2300;
    const lineHeight = 1.2;
    
    // Find optimal font size that fits all lines
    while (fontSize > 50) {
        ctx.font = `bold ${fontSize}px "${fontName}"`;
        
        let allLinesFit = true;
        for (const line of lines) {
            const metrics = ctx.measureText(line);
            if (metrics.width > maxWidth) {
                allLinesFit = false;
                break;
            }
        }
        
        if (allLinesFit) break;
        fontSize -= 10;
    }

    // Calculate total height needed
    const totalHeight = fontSize * lineHeight * lines.length;
    const startY = (canvas.height - totalHeight) / 2;

    // Draw each line
    ctx.font = `bold ${fontSize}px "${fontName}"`;
    ctx.textAlign = textAlign;
    
    let startX: number;
    if (textAlign === 'center') {
        startX = canvas.width / 2;
    } else if (textAlign === 'right') {
        startX = canvas.width - 100;
    } else {
        startX = 100;
    }
    
    lines.forEach((line, index) => {
        const y = startY + (fontSize * lineHeight * index);
        ctx.fillText(line, startX, y);
    });

    // 2. AUTO-CROP ALGORITHM: Remove transparent borders
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
    let found = false;

    // Scan for non-transparent pixels
    for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
            const alpha = data[(y * canvas.width + x) * 4 + 3];
            if (alpha > 20) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
                found = true;
            }
        }
    }

    // If nothing was drawn (all transparent)
    if (!found) return canvas.toDataURL("image/png");

    // 3. Create final canvas with tight crop
    const padding = 20; // Small padding for better appearance
    const croppedWidth = (maxX - minX) + (padding * 2);
    const croppedHeight = (maxY - minY) + (padding * 2);

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = croppedWidth;
    finalCanvas.height = croppedHeight;
    const fCtx = finalCanvas.getContext('2d');

    if (fCtx) {
        // Draw cropped region to new canvas
        fCtx.drawImage(
            canvas,
            minX, minY, maxX - minX, maxY - minY, // Source rect (text region)
            padding, padding, maxX - minX, maxY - minY // Dest rect
        );
        return finalCanvas.toDataURL("image/png");
    }

    return canvas.toDataURL("image/png");
};